import React, { useState } from 'react';
import { 
  Settings, 
  Moon, 
  Sun, 
  Bell, 
  Shield, 
  Download, 
  Trash2, 
  User, 
  Globe,
  Palette,
  Smartphone,
  Mail,
  Database,
  Key,
  Info,
  Save,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { notificationService } from '../../services/notificationService';
import { resendEmailService } from '../../services/resendEmailService';
import { useNavigate } from 'react-router-dom';

interface SettingsSection {
  id: string;
  title: string;
  icon: React.ComponentType<any>;
  description: string;
}

const settingsSections: SettingsSection[] = [
  {
    id: 'appearance',
    title: 'Aspetto',
    icon: Palette,
    description: 'Tema, colori e personalizzazione'
  },
  {
    id: 'notifications',
    title: 'Notifiche',
    icon: Bell,
    description: 'Gestisci le notifiche push ed email'
  },
  {
    id: 'privacy',
    title: 'Privacy e Sicurezza',
    icon: Shield,
    description: 'Impostazioni privacy e sicurezza account'
  },
  {
    id: 'data',
    title: 'Gestione Dati',
    icon: Database,
    description: 'Esporta, importa e gestisci i tuoi dati'
  },
  {
    id: 'account',
    title: 'Account',
    icon: User,
    description: 'Informazioni account e preferenze'
  },
  {
    id: 'system',
    title: 'Sistema',
    icon: Settings,
    description: 'Impostazioni avanzate del sistema'
  }
];

export const SettingsPage: React.FC = () => {
  const { theme, toggleTheme, setTheme } = useTheme();
  const { userProfile, user } = useAuthStore();
  const addNotification = useNotificationStore(s => s.addNotification);
  const [activeSection, setActiveSection] = useState('appearance');
  const [loading, setLoading] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(() => notificationService.getNotificationStatus());
  const [initializingNotifications, setInitializingNotifications] = useState(false);
  const [emailConfig, setEmailConfig] = useState(() => resendEmailService.getConfig());
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [resendApiKey, setResendApiKey] = useState('');
  const [resendFromEmail, setResendFromEmail] = useState('noreply@job.studioquovadis.ch');
  const [showResendConfig, setShowResendConfig] = useState(false);
  const navigate = useNavigate();

  // Impostazioni notifiche
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: userProfile?.preferences?.emailNotifications ?? true,
    pushNotifications: userProfile?.preferences?.pushNotifications ?? true,
    reminderNotifications: true,
    weeklyDigest: false,
    marketingEmails: false
  });

  // Impostazioni privacy
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'private',
    dataSharing: false,
    analytics: true,
    cookies: true
  });

  // Verifica il ruolo dell'utente per mostrare le sezioni appropriate
  const userRole = userProfile?.role || 'user';
  const isAdmin = userRole === 'admin';
  const isModerator = userRole === 'moderator';
  
  // Filtra le sezioni in base al ruolo
  const getVisibleSections = () => {
    const isNormalUser = userRole === 'user';
    
    if (isNormalUser) {
      return settingsSections.filter(section => 
        section.id === 'appearance' || 
        section.id === 'notifications' || 
        section.id === 'account'
      );
    }
    
    // Per admin e moderator, nascondi le sezioni "Dati" e "Sistema" ai moderator
    if (userProfile?.role === 'moderator') {
      return settingsSections.filter(section => 
        section.id !== 'data' && section.id !== 'system'
      );
    }
    
    return settingsSections;
  };

  // Se la sezione attiva non è più disponibile, cambia alla prima disponibile
  React.useEffect(() => {
    const visibleSections = getVisibleSections();
    if (!visibleSections.find(s => s.id === activeSection)) {
      setActiveSection(visibleSections[0]?.id || 'appearance');
    }
  }, [userProfile?.role, activeSection]);

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      // Simula salvataggio
      await new Promise(resolve => setTimeout(resolve, 1000));
      addNotification({
        type: 'info',
        title: 'Impostazioni salvate',
        message: 'Le tue preferenze sono state aggiornate con successo.'
      });
    } catch (error) {
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Impossibile salvare le impostazioni. Riprova più tardi.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    // Simula esportazione dati
    const data = {
      user: userProfile,
      applications: [], // Qui andrebbero le candidature
      settings: {
        notifications: notificationSettings,
        privacy: privacySettings,
        theme: theme
      },
      exportDate: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jobtracker-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    addNotification({
      type: 'info',
      title: 'Dati esportati',
      message: 'I tuoi dati sono stati scaricati con successo.'
    });
  };

  const handleClearData = () => {
    if (window.confirm('Sei sicuro di voler cancellare tutti i dati? Questa azione è irreversibile.')) {
      // Simula cancellazione dati
      addNotification({
        type: 'warning',
        title: 'Dati cancellati',
        message: 'Tutti i dati sono stati rimossi dal sistema.'
      });
    }
  };

  const handleInitializeNotifications = async () => {
    if (!user?.uid) {
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Utente non autenticato.'
      });
      return;
    }

    setInitializingNotifications(true);
    try {
      const success = await notificationService.initializeNotifications(user.uid);
      
      if (success) {
        setNotificationStatus(notificationService.getNotificationStatus());
        setNotificationSettings(prev => ({ ...prev, pushNotifications: true }));
        addNotification({
          type: 'info',
          title: 'Notifiche attivate',
          message: 'Le notifiche push sono state configurate con successo!'
        });
      } else {
        addNotification({
          type: 'warning',
          title: 'Errore',
          message: 'Impossibile attivare le notifiche. Controlla i permessi del browser.'
        });
      }
    } catch (error) {
      console.error('Errore nell\'inizializzazione notifiche:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Errore durante l\'attivazione delle notifiche.'
      });
    } finally {
      setInitializingNotifications(false);
    }
  };

  const handleTestNotification = async () => {
    if (!user?.uid) {
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Utente non autenticato.'
      });
      return;
    }

    try {
      await notificationService.sendTestNotificationViaFCM(user.uid);
      addNotification({
        type: 'info',
        title: 'Notifica di test inviata',
        message: 'Controlla se hai ricevuto la notifica push!'
      });
    } catch (error) {
      console.error('Errore nell\'invio notifica di test:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Impossibile inviare la notifica di test.'
      });
    }
  };

  const handleTogglePushNotifications = async (enabled: boolean) => {
    if (!user?.uid) {
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Utente non autenticato.'
      });
      return;
    }

    try {
      if (enabled) {
        await handleInitializeNotifications();
      } else {
        await notificationService.deleteToken();
        setNotificationStatus(notificationService.getNotificationStatus());
        addNotification({
          type: 'info',
          title: 'Notifiche disattivate',
          message: 'Le notifiche push sono state disattivate.'
        });
      }
    } catch (error) {
      console.error('Errore nel toggle notifiche:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Impossibile modificare le impostazioni notifiche.'
      });
    }
  };

  const handleTestEmail = async () => {
    if (!userProfile?.email || !userProfile?.name) {
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Email o nome utente non disponibili.'
      });
      return;
    }

    if (!resendEmailService.isServiceConfigured()) {
      addNotification({
        type: 'warning',
        title: 'Resend non configurato',
        message: 'Configura Resend per inviare email di test.'
      });
      return;
    }

    setSendingTestEmail(true);
    try {
      const success = await resendEmailService.sendTestEmail(userProfile.email, userProfile.name);
      
      if (success) {
        addNotification({
          type: 'info',
          title: 'Email di test inviata',
          message: 'Controlla la tua casella email per il reminder di test!'
        });
      } else {
        addNotification({
          type: 'warning',
          title: 'Errore',
          message: 'Impossibile inviare l\'email di test.'
        });
      }
    } catch (error) {
      console.error('Errore nell\'invio email di test:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Errore durante l\'invio dell\'email di test.'
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleDebugResend = async () => {
    try {
      const isConfigured = resendEmailService.isServiceConfigured();
      addNotification({
        type: 'info',
        title: 'Configurazione Resend',
        message: `Resend ${isConfigured ? 'configurato' : 'non configurato'}`
      });
    } catch (error) {
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Impossibile verificare la configurazione Resend'
      });
    }
  };

  const handleConfigureResend = () => {
    if (resendApiKey.trim()) {
      resendEmailService.configure(resendApiKey.trim(), resendFromEmail.trim());
      setShowResendConfig(false);
      addNotification({
        type: 'info',
        title: 'Resend Configurato',
        message: `API key Resend configurata con successo! Email di invio: ${resendFromEmail.trim()}`
      });
    } else {
      addNotification({
        type: 'warning',
        title: 'API Key Richiesta',
        message: 'Inserisci una API key Resend valida.'
      });
    }
  };

  const handleTestResend = async () => {
    if (!userProfile?.email || !userProfile?.name) {
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Email o nome utente non disponibili.'
      });
      return;
    }

    if (!resendEmailService.isServiceConfigured()) {
      addNotification({
        type: 'warning',
        title: 'Resend non configurato',
        message: 'Configura Resend per inviare email di test.'
      });
      return;
    }

    setSendingTestEmail(true);
    try {
      const success = await resendEmailService.sendTestEmail(userProfile.email, userProfile.name);
      
      if (success) {
        addNotification({
          type: 'info',
          title: 'Email di test inviata',
          message: 'Controlla la tua casella email per il reminder di test!'
        });
      } else {
        addNotification({
          type: 'warning',
          title: 'Errore',
          message: 'Impossibile inviare l\'email di test. Controlla la console per i dettagli.'
        });
      }
    } catch (error) {
      console.error('Errore nell\'invio email di test:', error);
      addNotification({
        type: 'warning',
        title: 'Errore',
        message: 'Errore durante l\'invio dell\'email di test.'
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleVerifyResend = async () => {
    if (!resendEmailService.isServiceConfigured()) {
      addNotification({
        type: 'warning',
        title: 'Resend non configurato',
        message: 'Configura Resend prima di verificare.'
      });
      return;
    }

    try {
      const config = await resendEmailService.verifyConfiguration();
      console.log('🔍 Verifica configurazione Resend:', config);
      
      if (config.error) {
        addNotification({
          type: 'warning',
          title: 'Resend - Errore Configurazione',
          message: config.error
        });
      } else if (config.testResult) {
        addNotification({
          type: 'info',
          title: 'Resend - Configurazione OK',
          message: 'API key valida e test email inviato con successo!'
        });
      } else {
        addNotification({
          type: 'warning',
          title: 'Resend - Test Fallito',
          message: 'API key valida ma test email fallito. Controlla la console.'
        });
      }
    } catch (error) {
      console.error('Errore verifica Resend:', error);
      addNotification({
        type: 'warning',
        title: 'Errore Verifica Resend',
        message: 'Errore durante la verifica della configurazione.'
      });
    }
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'appearance':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tema</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setTheme('light')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    theme === 'light' 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Sun className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                  <p className="font-medium text-gray-900 dark:text-white">Chiaro</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Tema luminoso</p>
                </button>
                
                <button
                  onClick={() => setTheme('dark')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    theme === 'dark' 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Moon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <p className="font-medium text-gray-900 dark:text-white">Scuro</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Tema scuro</p>
                </button>
                
                <button
                  onClick={toggleTheme}
                  className="p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all"
                >
                  <RefreshCw className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                  <p className="font-medium text-gray-900 dark:text-white">Auto</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Sistema</p>
                </button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Personalizzazione</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Animazioni</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Mostra animazioni e transizioni</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Riduci movimento</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Disabilita animazioni per accessibilità</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-8">
            {/* Header sezione */}
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Notifiche</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Gestisci come ricevere aggiornamenti</p>
            </div>

            {/* Notifiche Push */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <Bell className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Notifiche Push</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Ricevi notifiche in tempo reale</p>
                </div>
              </div>
              
              {/* Stato notifiche */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stato browser:</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    notificationStatus.supported && notificationStatus.permission === 'granted'
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : 'bg-red-100 text-red-800 border-red-200'
                  }`}>
                    {notificationStatus.supported && notificationStatus.permission === 'granted' ? 'Attive' : 'Non disponibili'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <span>Supporto:</span>
                    <span className={notificationStatus.supported ? 'text-green-600' : 'text-red-600'}>
                      {notificationStatus.supported ? 'Sì' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span>Permessi:</span>
                    <span className="font-medium">{notificationStatus.permission}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span>Token:</span>
                    <span className={notificationStatus.token ? 'text-green-600' : 'text-red-600'}>
                      {notificationStatus.token ? 'Sì' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span>Service Worker:</span>
                    <span className={notificationStatus.serviceWorkerRegistered ? 'text-green-600' : 'text-red-600'}>
                      {notificationStatus.serviceWorkerRegistered ? 'Sì' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Bell className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Notifiche push</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Ricevi notifiche in tempo reale</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.pushNotifications && notificationStatus.supported && notificationStatus.permission === 'granted'}
                      onChange={(e) => handleTogglePushNotifications(e.target.checked)}
                      disabled={!notificationStatus.supported || initializingNotifications}
                      className="sr-only peer" 
                    />
                    <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer transition-colors ${
                      notificationSettings.pushNotifications && notificationStatus.supported && notificationStatus.permission === 'granted'
                        ? 'bg-blue-600 peer-checked:after:translate-x-full'
                        : 'bg-gray-200 dark:bg-gray-700'
                    } peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600`}></div>
                  </label>
                </div>

                {/* Pulsante per attivare le notifiche se non sono attive */}
                {notificationStatus.supported && notificationStatus.permission !== 'granted' && (
                  <button
                    onClick={handleInitializeNotifications}
                    disabled={initializingNotifications}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm font-medium"
                  >
                    {initializingNotifications ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        <span>Attivazione...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <Bell className="w-4 h-4" />
                        <span>Attiva Notifiche Push</span>
                      </div>
                    )}
                  </button>
                )}

                {/* Pulsanti test notifica */}
                {notificationSettings.pushNotifications && notificationStatus.supported && notificationStatus.permission === 'granted' && (
                  <div className="space-y-3">
                    <button
                      onClick={handleTestNotification}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        <span>Invia Notifica di Test</span>
                      </div>
                    </button>
                    
                    <button
                      onClick={async () => {
                        try {
                          const config = await notificationService.verifyConfiguration();
                          console.log('Configurazione notifiche:', config);
                          addNotification({
                            type: 'info',
                            title: 'Configurazione Notifiche',
                            message: `Supporto: ${config.supported ? 'Sì' : 'No'} | Permessi: ${config.permission} | Token: ${config.token ? 'Sì' : 'No'} | Service Worker: ${config.serviceWorker ? 'Sì' : 'No'} | VAPID Key: ${config.vapidKey ? 'Sì' : 'No'}`
                          });
                        } catch (error) {
                          console.error('Errore verifica configurazione:', error);
                        }
                      }}
                      className="w-full px-4 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Verifica Configurazione</span>
                      </div>
                    </button>
                  </div>
                )}
                
                <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Reminder colloqui</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Promemoria per i colloqui programmati</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.reminderNotifications}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, reminderNotifications: e.target.checked }))}
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Notifiche Email */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Notifiche Email</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Email automatiche e reminder</p>
                </div>
              </div>
              
              {/* Stato Resend - solo per admin/moderator */}
              {!isAdmin && !isModerator && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stato Resend:</span>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      resendEmailService.isServiceConfigured()
                        ? 'bg-green-100 text-green-800 border-green-200'
                        : 'bg-red-100 text-red-800 border-red-200'
                    }`}>
                      {resendEmailService.isServiceConfigured() ? 'Configurato' : 'Non configurato'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {resendEmailService.isServiceConfigured() 
                      ? 'Resend configurato correttamente per i reminder candidature'
                      : 'Configura Resend per inviare email di reminder automatici'
                    }
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Reminder candidature</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Email automatica dopo 7 giorni senza risposta</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={notificationSettings.emailNotifications && resendEmailService.isServiceConfigured()}
                      onChange={(e) => setNotificationSettings(prev => ({ ...prev, emailNotifications: e.target.checked }))}
                      disabled={!resendEmailService.isServiceConfigured()}
                      className="sr-only peer" 
                    />
                    <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer transition-colors ${
                      notificationSettings.emailNotifications && resendEmailService.isServiceConfigured()
                        ? 'bg-purple-600 peer-checked:after:translate-x-full'
                        : 'bg-gray-200 dark:bg-gray-700'
                    } peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600`}></div>
                  </label>
                </div>

                {/* Debug Ruolo Utente - Solo per sviluppo */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200/50 dark:border-yellow-700/50">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Debug Ruolo (Solo Sviluppo)
                        </p>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                          Role: {userProfile?.role || 'undefined'} | 
                          Email: {userProfile?.email || 'undefined'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Configurazione Resend - Solo per admin */}
                {userProfile?.role === 'admin' && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        Alternativa Gratuita: Resend
                      </h4>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-800 border-green-200 rounded-full font-medium">
                        3.000 email/mese
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      EmailJS è diventato a pagamento. <strong>Resend</strong> offre 3.000 email/mese gratuite con API semplice!
                    </p>
                    
                    {!showResendConfig ? (
                      <button
                        onClick={() => setShowResendConfig(true)}
                        className="w-full px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Configura Resend</span>
                        </div>
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <input
                          type="password"
                          placeholder="Inserisci API Key Resend (re_...)"
                          value={resendApiKey}
                          onChange={(e) => setResendApiKey(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white text-sm"
                        />
                        <input
                          type="email"
                          placeholder="Email di invio (es: noreply@job.studioquovadis.ch)"
                          value={resendFromEmail}
                          onChange={(e) => setResendFromEmail(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-white text-sm"
                        />
                        <div className="flex space-x-3">
                          <button
                            onClick={handleConfigureResend}
                            className="flex-1 px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-medium"
                          >
                            <div className="flex items-center justify-center space-x-2">
                              <Save className="w-4 h-4" />
                              <span>Salva Configurazione</span>
                            </div>
                          </button>
                          <button
                            onClick={() => setShowResendConfig(false)}
                            className="px-4 py-3 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors text-sm font-medium"
                          >
                            Annulla
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {resendEmailService.isServiceConfigured() && (
                      <div className="mt-4 p-4 bg-green-100 dark:bg-green-800/30 rounded-xl border border-green-200 dark:border-green-700">
                        <div className="flex items-center justify-center space-x-2 mb-3">
                          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">
                            Resend configurato correttamente
                          </p>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={handleVerifyResend}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                          >
                            <div className="flex items-center justify-center space-x-2">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Verifica Config</span>
                            </div>
                          </button>
                          <button
                            onClick={handleTestResend}
                            disabled={sendingTestEmail}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                          >
                            {sendingTestEmail ? (
                              <div className="flex items-center justify-center space-x-2">
                                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                <span>Invio...</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center space-x-2">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                                <span>Test Email</span>
                              </div>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Pulsanti test email - solo per admin */}
                {userProfile?.role === 'admin' && resendEmailService.isServiceConfigured() && (
                  <div className="space-y-3 mt-6">
                    <button
                      onClick={handleTestEmail}
                      disabled={sendingTestEmail}
                      className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-medium shadow-lg disabled:opacity-50"
                    >
                      {sendingTestEmail ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>Invio...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <Mail className="w-4 h-4" />
                          <span>Invia Email di Test</span>
                        </div>
                      )}
                    </button>
                    
                    <button
                      onClick={handleDebugResend}
                      className="w-full px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 font-medium shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>Debug Resend</span>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleVerifyResend}
                      className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 font-medium shadow-lg"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Verifica Configurazione Resend</span>
                      </div>
                    </button>
                  </div>
                )}

                {/* Info box - Solo per utenti normali */}
                {userRole === 'user' && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200/50 dark:border-blue-700/50">
                    <div className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                          Come funziona
                        </p>
                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                          Riceverai automaticamente un'email di reminder per ogni candidatura che non ha ricevuto risposta dopo 7 giorni.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Privacy</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Visibilità profilo
                  </label>
                  <select 
                    value={privacySettings.profileVisibility}
                    onChange={(e) => setPrivacySettings(prev => ({ ...prev, profileVisibility: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="private">Privato</option>
                    <option value="public">Pubblico</option>
                    <option value="friends">Solo amici</option>
                  </select>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Condivisione dati</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Consenti l'uso dei dati per miglioramenti</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={privacySettings.dataSharing}
                      onChange={(e) => setPrivacySettings(prev => ({ ...prev, dataSharing: e.target.checked }))}
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Sicurezza</h3>
              <div className="space-y-4">
                <button className="w-full px-4 py-2 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Cambia password</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Aggiorna la tua password</p>
                    </div>
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                </button>
                
                <button className="w-full px-4 py-2 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Autenticazione a due fattori</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Aggiungi un livello di sicurezza</p>
                    </div>
                    <Shield className="h-5 w-5 text-gray-400" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        );

      case 'data':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Esporta Dati</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Scarica tutti i tuoi dati in formato JSON. Include candidature, impostazioni e informazioni del profilo.
              </p>
              <button
                onClick={handleExportData}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="h-5 w-5" />
                <span>Esporta Dati</span>
              </button>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Importa Dati</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Carica un file JSON per ripristinare i tuoi dati da un backup.
              </p>
              <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                <Download className="h-5 w-5" />
                <span>Importa Dati</span>
              </button>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Zona Pericolosa</h3>
              <div className="space-y-4">
                <button
                  onClick={handleClearData}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-5 w-5" />
                  <span>Cancella Tutti i Dati</span>
                </button>
                
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  ⚠️ Questa azione è irreversibile. Tutti i tuoi dati verranno eliminati permanentemente.
                </p>
              </div>
            </div>
          </div>
        );

      case 'account':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informazioni Account</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nome
                  </label>
                  <p className="text-gray-900 dark:text-white">{userProfile?.name || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <p className="text-gray-900 dark:text-white">{userProfile?.email || 'N/A'}</p>
                </div>
                

                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data registrazione
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {userProfile?.createdAt?.toDate ? userProfile.createdAt.toDate().toLocaleDateString('it-IT') : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Preferenze di Ricerca Lavoro */}
            {userProfile?.jobPreferences && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">🎯 Preferenze di Ricerca Lavoro</h3>
                <div className="space-y-4">
                  {/* Posizioni Ricercate */}
                  {userProfile.jobPreferences.positions && userProfile.jobPreferences.positions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        💼 Posizioni Ricercate
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {userProfile.jobPreferences.positions.map((position, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm font-medium"
                          >
                            {position}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Livello di Esperienza */}
                  {userProfile.jobPreferences.experienceLevel && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        📈 Livello di Esperienza
                      </label>
                      <p className="text-gray-900 dark:text-white">
                        {userProfile.jobPreferences.experienceLevel}
                      </p>
                    </div>
                  )}

                  {/* Preferenza Lavoro */}
                  {userProfile.jobPreferences.remotePreference && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        🏠 Preferenza Lavoro
                      </label>
                      <p className="text-gray-900 dark:text-white">
                        {userProfile.jobPreferences.remotePreference}
                      </p>
                    </div>
                  )}

                  {/* Settore Preferito */}
                  {userProfile.jobPreferences.preferredSector && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        🏢 Settore Preferito
                      </label>
                      <p className="text-gray-900 dark:text-white">
                        {userProfile.jobPreferences.preferredSector}
                      </p>
                    </div>
                  )}

                  {/* Descrizione Dettagliata */}
                  {userProfile.jobPreferences.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        📝 Descrizione Dettagliata
                      </label>
                      <p className="text-gray-900 dark:text-white text-sm leading-relaxed">
                        {userProfile.jobPreferences.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Azioni Account</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => navigate('/profile')}
                  className="w-full px-4 py-2 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Modifica profilo</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Aggiorna informazioni personali</p>
                    </div>
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                </button>
                
                {/* Pulsante elimina account - solo per admin/moderator */}
                {userRole !== 'user' && (
                  <button className="w-full px-4 py-2 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Elimina account</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Cancella definitivamente il tuo account</p>
                      </div>
                      <Trash2 className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      case 'system':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informazioni Sistema</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Versione App</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">1.0.0</p>
                  </div>
                  
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Tema Attuale</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">{theme}</p>
                  </div>
                  
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Browser</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{navigator.userAgent.split(' ').pop()}</p>
                  </div>
                  
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Piattaforma</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{navigator.platform}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Azioni Sistema</h3>
              <div className="space-y-2">
                <button className="w-full px-4 py-2 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Pulisci cache</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Rimuovi dati temporanei</p>
                    </div>
                    <RefreshCw className="h-5 w-5 text-gray-400" />
                  </div>
                </button>
                
                <button className="w-full px-4 py-2 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Ripristina impostazioni</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Riporta tutto ai valori predefiniti</p>
                    </div>
                    <Settings className="h-5 w-5 text-gray-400" />
                  </div>
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Sezione non trovata</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      {/* Header con Welcome e Azioni Rapide */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 lg:gap-3 mb-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-1.5 lg:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg lg:rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5 lg:w-6 lg:h-6" />
              </button>
              <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg lg:rounded-xl">
                <Settings className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">Impostazioni</h1>
                <p className="text-sm lg:text-base text-gray-600">Gestisci le tue preferenze e configurazioni</p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <button
              onClick={handleSaveSettings}
              disabled={loading}
              className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-blue-600 text-white rounded-lg lg:rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm lg:text-base"
            >
              <Save className="w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2" />
              {loading ? 'Salvando...' : 'Salva'}
            </button>
          </div>
        </div>
      </div>

      {/* Statistiche Principali - Mobile: 2x2, Desktop: 1x4 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Sezioni</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{getVisibleSections().length}</p>
              <p className="text-xs text-gray-500 mt-1">Disponibili</p>
            </div>
            <div className="p-2 lg:p-3 bg-blue-100 rounded-lg lg:rounded-xl">
              <Settings className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Notifiche</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">
                {notificationSettings.pushNotifications ? 'On' : 'Off'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Push</p>
            </div>
            <div className="p-2 lg:p-3 bg-green-100 rounded-lg lg:rounded-xl">
              <Bell className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Tema</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900 capitalize">{theme}</p>
              <p className="text-xs text-gray-500 mt-1">Attivo</p>
            </div>
            <div className="p-2 lg:p-3 bg-purple-100 rounded-lg lg:rounded-xl">
              <Palette className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Ruolo</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900 capitalize">{userRole}</p>
              <p className="text-xs text-gray-500 mt-1">Utente</p>
            </div>
            <div className="p-2 lg:p-3 bg-yellow-100 rounded-lg lg:rounded-xl">
              <User className="w-5 h-5 lg:w-6 lg:h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="flex overflow-x-auto gap-2 lg:gap-3 scrollbar-hide">
          {getVisibleSections().map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex flex-col items-center px-4 lg:px-6 py-3 lg:py-4 rounded-xl transition-all whitespace-nowrap text-sm font-medium min-w-[80px] lg:min-w-[100px] shadow-lg hover:shadow-xl
                ${activeSection === section.id 
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white transform scale-105' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 border border-gray-200 hover:border-blue-300'
                }`}
            >
              <section.icon className={`w-5 h-5 lg:w-6 lg:h-6 mb-2 ${activeSection === section.id ? 'text-white' : 'text-gray-600'}`} />
              <span className="font-medium text-xs lg:text-sm">{section.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Sezione Attiva */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        {renderSection()}
      </div>
    </div>
  );
}; 